export enum AgentActionTypes {
  SHOW = '@@agent/SHOW',
  UPDATE_AVAILABLE_AGENTS = '@@agent/UPDATE_AVAILABLE_AGENTS',
  SET_SELECTED_AGENT = '@@agent/SET_SELECTED_AGENT',
  UPDATE_SELECTED_AGENT = '@@agent/UPDATE_SELECTED_AGENT',
  UPDATE_CHAT_SESSIONS = '@@agent/UPDATE_CHAT_SESSIONS',
  UPDATE_SESSION_MESSAGE = '@@agent/UPDATE_SESSION_MESSAGE',
  CHAT_WITH_AGENT = '@@agent/CHAT_WITH_AGENT',
  STOP_AGENT = '@@agent/STOP_AGENT',
  SET_STOPPING_STATE = '@@agent/SET_STOPPING_STATE',
  ADD_CONTEXT = '@@agent/ADD_CONTEXT',
  REMOVE_CONTEXT = '@@agent/REMOVE_CONTEXT',
  CLEAR_CONTEXT = '@@agent/CLEAR_CONTEXT',
  CLEAR_SESSION = '@@agent/CLEAR_SESSION',
  CLEAR_ALL_SESSIONS = '@@agent/CLEAR_ALL_SESSIONS',
  RESET_AGENT_DATA = '@@agent/RESET_AGENT_DATA',
  SET_CURRENT_SESSION_CODE = '@@agent/SET_CURRENT_SESSION_CODE',
  LOAD_SESSION_HISTORY = '@@agent/LOAD_SESSION_HISTORY',
  SET_SESSION_HISTORY = '@@agent/SET_SESSION_HISTORY',
  RESUME_SESSION = '@@agent/RESUME_SESSION',
  DELETE_SESSION = '@@agent/DELETE_SESSION',
  RENAME_SESSION = '@@agent/RENAME_SESSION',
  RESTORE_CHECKPOINT = '@@agent/RESTORE_CHECKPOINT',
  START_COMPONENT_CONTEXT_PICKER = '@@agent/START_COMPONENT_CONTEXT_PICKER',
  STOP_COMPONENT_CONTEXT_PICKER = '@@agent/STOP_COMPONENT_CONTEXT_PICKER',
  COMPACT_SESSION = '@@agent/COMPACT_SESSION',
  ATTACH_SAVED_MEMORY = '@@agent/ATTACH_SAVED_MEMORY'
}
export interface ContextSettings {
  enable_nodes: boolean
  min_nodes: number
  max_nodes: number
  enable_components: boolean
  min_components: number
  max_components: number
  allowed_component_types?: string[]
  enable_files: boolean
  allowed_extensions?: string[]
}
export enum EnableOnCondition {
  INTERFACES_EDIT_MODE = 'interfaces_edit_mode',
  CODE_NODE_IS_CALC = 'code_node_is_calc'
}
/**
 * Machine-readable cause behind `Agent.disabled`, so the UI can explain it
 * instead of falling back to the generic "not available in this section".
 * The AI-availability values mirror `AiBlockReason` in common/types/aiSettings.
 */
export enum AgentDisabledReason {
  AiDisabledForCompany = 'ai_disabled_for_company',
  MonthlyTokenLimitReached = 'monthly_token_limit_reached',
  /** The agent is defined inside the app and the company has those switched off. */
  CustomAgentsDisabled = 'custom_agents_disabled',
  RequiresAppOpen = 'requires_app_open'
}

export interface Agent {
  code: string
  name: string
  provider?: string
  model?: string
  description?: string
  system?: boolean
  disabled?: boolean
  disabled_reason?: AgentDisabledReason | null
  render_html_blocks?: boolean
  context_settings: ContextSettings
  enable_on_sections?: string[]
  enable_on_conditions?: EnableOnCondition[]
  conditions?: AgentConditions
}

export interface AgentConditions {
  must_have_app_open?: boolean
}

export interface AgentState {
  readonly show?: boolean
  readonly availableAgents?: Agent[]
  readonly selectedAgent?: Agent
  readonly currentSessionCode?: string
  readonly sessions?: ChatSessions
  readonly sessionHistory?: SessionSummary[]
  readonly sessionHistoryLoading?: boolean
  readonly componentContextPicker?: ComponentContextPicker
}

export enum AgentStatus {
  WAITING = 'WAITING',
  REASONING = 'REASONING',
  SEARCHING = 'SEARCHING',
  GENERATING = 'GENERATING',
  RESPONDING = 'RESPONDING',
  USING_TOOL = 'USING_TOOL',
  EXECUTING_TOOL = 'EXECUTING_TOOL',
  COMPLETED = 'COMPLETED',
  INDEXING_DOCUMENTS = 'INDEXING_DOCUMENTS',
  EXECUTING_CONTEXT_NODES = 'EXECUTING_CONTEXT_NODES',
  TRACE_CREATED = 'TRACE_CREATED',
  DOCUMENTING_PROGRESS = 'DOCUMENTING_PROGRESS',
  COMPACTING = 'COMPACTING',
  /**
   * Something was written to the application memory. Arrives *after* COMPLETED
   * — the backend decides what persists once the answer is already streamed —
   * so it is attached to the finished message instead of being chat text.
   */
  MEMORY_SAVED = 'MEMORY_SAVED'
}

/** A memory the backend stored in the open application. */
export interface SavedMemory {
  id: string
  text: string
  category: string
}

export enum AgentChatRole {
  user = 'user',
  assistant = 'assistant',
  reasoning = 'reasoning',
  tool_call = 'tool_call',
  question = 'question'
}

/**
 * A file the agent generated for the user during a turn. Identified by name
 * only: the download URL is minted when the user clicks, so nothing that
 * expires is ever stored in the conversation.
 */
export interface GeneratedFile {
  name: string
  size_bytes: number
  content_type: string
}

export interface AgentQuestionOption {
  label: string
  description: string
}

/** A multiple-choice question the agent ended its turn on. */
export interface AgentQuestion {
  question: string
  options: AgentQuestionOption[]
  multi_select: boolean
}

export interface AgentChatMessage {
  role: AgentChatRole
  content?: string
  toolName?: string
  traceId?: string
  /** Memory stored while producing this answer. Never claimed by the agent
   * itself: this is the only thing that means it actually happened. */
  savedMemory?: SavedMemory
  /** Files generated while producing this answer, shown as download buttons. */
  generatedFiles?: GeneratedFile[]
  question?: AgentQuestion
}

export enum AgentCode {
  // The single unified chat agent.
  PYPLAN = 'PYPLAN',
  // Inline / structured-output workers invoked by UI editors (not chat agents,
  // not shown in the selector).
  INLINE_DEVELOPER = 'INLINE_DEVELOPER',
  HTML_DEVELOPER = 'HTML_DEVELOPER',
  CUSTOM_COMPONENT_DEVELOPER = 'CUSTOM_COMPONENT_DEVELOPER',
  HTML_INTERFACE_DEVELOPER = 'HTML_INTERFACE_DEVELOPER'
}

/**
 * Context-budget usage of the conversation, reported by the API after each
 * turn. `percent` reaches 100 when the session hits its compaction budget
 * (the API then summarizes older messages automatically).
 */
export interface ContextUsage {
  used_tokens: number
  budget_tokens: number
  percent: number
}

export interface ChatSession {
  chatSessionId: string
  chatSessionCode: string
  agentCode: AgentCode
  context?: Record<string, any>
  metadata?: Record<string, any>
  messages?: AgentChatMessage[]
  draftMessage?: string
  status?: AgentStatus
  isStopping?: boolean
  reasoningText?: string
  toolName?: string
  contextUsage?: ContextUsage
}

export interface AddChatContextOptions {
  isSingle?: boolean
  replaceUnpinned?: boolean
}

/**
 * Active state of the "pick an interface component" mode. When present, the
 * agent chat is waiting for the user to click a widget in the interface to add
 * it to the chat context. `agentCode` is the agent the picked component will be
 * added to; `isSingle` mirrors `max_components === 1` (single-slot context).
 */
export interface ComponentContextPicker {
  agentCode: string
  isSingle?: boolean
}

export interface ClearChatContextOptions {
  onlyUnpinned?: boolean
}

export interface ChatSessions {
  [key: string]: ChatSession
}

export interface SessionSummary {
  chat_session_id: string
  agent_code: string
  instance_id: string
  title: string | null
  created_at: string
  updated_at: string
  message_count: number
}
